import { clsx } from 'clsx'
import React, { ButtonHTMLAttributes, CSSProperties, ReactNode, forwardRef } from 'react'

import { ComponentColor, ComponentColorVariants, ComponentSize } from '../../types'

import classes from './Button.module.scss'

type ButtonVariant = 'filled' | 'outlined' | 'ghost'
type ButtonColorVariant = {
  bg: string | number
  bgHover: string | number
  bgActive: string | number
  color: string | number
  border: string | number
}

const buttonColorVariants: ComponentColorVariants<ButtonVariant, ButtonColorVariant> = {
  filled: { bg: 600, bgHover: 700, bgActive: 800, color: 0, border: 'none' },
  outlined: { bg: 'none', bgHover: 50, bgActive: 100, color: 600, border: 600 },
  ghost: { bg: 'none', bgHover: 50, bgActive: 100, color: 600, border: 'none' },
}

type ButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> & {
  variant?: ButtonVariant
  color?: ComponentColor
  size?: ComponentSize
  leftSection?: ReactNode
  rightSection?: ReactNode
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'filled',
      color = 'blue',
      size = 'md',
      leftSection,
      rightSection,
      className,
      children,
      style,
      ...props
    },
    ref
  ) => {
    const styles = {
      '--button-height': `var(--button-height-${size})`,
      '--button-background': `var(--color-${color}-${buttonColorVariants[variant].bg})`,
      '--button-hover': `var(--color-${color}-${buttonColorVariants[variant].bgHover})`,
      '--button-active': `var(--color-${color}-${buttonColorVariants[variant].bgActive})`,
      '--button-color': `var(--color-${color}-${buttonColorVariants[variant].color})`,
      '--button-border': `var(--color-${color}-${buttonColorVariants[variant].border})`,
      '--button-font': `var(--font-size-${size})`,
      '--button-spacing': `var(--spacing-${size})`,
      ...style,
    } as CSSProperties

    return (
      <button
        className={clsx(classes.root, className)}
        ref={ref}
        style={styles}
        {...(leftSection && { 'data-left': true })}
        {...(rightSection && { 'data-right': true })}
        {...(!children && { 'data-empty': true })}
        {...props}
      >
        {leftSection}
        {children}
        {rightSection}
      </button>
    )
  }
)

Button.displayName = 'Button'

export default Button
